
import * as Material from "@mui/material";
import { Text } from "@fragments";
import "./styles.scss";

/**
 * TabsSelector Component - A reusable tabs selector using DTO pattern
 * 
 * @param {Object} props
 * @param {Array<import('./TabDTO').TabDTO>} props.tabs - Array of tab objects
 * @param {string} props.value - The key of the currently active tab
 * @param {string} [props.className=''] - Additional CSS classes to append to tabs-selector
 * @param {Function} props.onChange - Callback function called with the clicked tab key
 * 
 * @example
 * <TabsSelector
 *   tabs={[
 *     { key: 'tab1', label: 'Tab 1' },
 *     { key: 'tab2', label: 'Tab 2', badge: 'New' }
 *   ]}
 *   value="tab1"
 *   className="my-custom-class"
 *   onChange={(key) => handleTabChange(key)}
 * />
 */
export default function TabsSelector({ tabs, value, className = '', onChange }) {
    const handleChange = (event, newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    };

    return (
        <div className={`tabs-selector mt-4 ${className}`.trim()}>
            <Material.Tabs
                value={value}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons={false}
                TabIndicatorProps={{
                    style: {
                        backgroundColor: 'var(--surface-2)',
                        height: '2px',
                        bottom: '0px'
                    }
                }}
            >
                {tabs.map(tab => {
                    const active = value === tab.key;
                    return (
                        <Material.Tab
                            key={tab.key}
                            label={
                                tab.badge ? (
                                    <div className="tab-with-badge">
                                        <Text.h5 contrast={5} weight={300} className="tab-title">
                                            {tab.label}
                                        </Text.h5>
                                        <span className="coming-soon-badge">
                                            {tab.badge}
                                        </span>
                                    </div>
                                ) : (
                                    <Text.h5
                                        contrast={active ? 10 : 5}
                                        weight={active ? 400 : 300}
                                        className="tab-title"
                                    >
                                        {tab.label}
                                    </Text.h5>
                                )
                            }
                            value={tab.key}
                            className={`${active ? 'current-tab' : ''}`.trim()}
                            disabled={tab.disabled}
                        />
                    );
                })}
            </Material.Tabs>
        </div>
    );
}
